import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Flame, CheckSquare, Trophy, ArrowRight, Phone, Users, Monitor, Mail } from 'lucide-react';
import crm, { STAGES, TASK_TYPES, crmUser } from '../crm/api';

// Follow-ups are typed — show which kind, so "due today" reads as real work.
const TICON = { call: Phone, meeting: Users, demo: Monitor, email: Mail, todo: CheckSquare };
const tLabel = (t) => (TASK_TYPES.find((x) => x.key === t) || {}).label || 'To-do';

// /crm-api/overview and /crm-api/tasks are already scoped to the caller by the
// API, so this is the same data the admin dashboard uses — just this rep's slice.
const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

export default function EmployeeHome() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [due, setDue] = useState([]);
  const me = crmUser() || {};

  useEffect(() => {
    crm.get('/overview').then(({ data }) => setD(data)).catch(() => setD({}));
    crm.get('/tasks', { params: { filter: 'today' } }).then(({ data }) => setDue(data.tasks || [])).catch(() => {});
  }, []);
  if (!d) return <div className="pt-10 text-[var(--muted)]">Loading…</div>;

  const kpis = [
    { label: 'My Leads', value: d.total || 0, icon: Target, from: '#2dd4bf', to: '#0891b2', cap: 'assigned to me', go: '/employee/leads' },
    { label: 'Hot', value: d.hot || 0, icon: Flame, from: '#fb923c', to: '#ef4444', cap: 'need attention', go: '/employee/leads' },
    { label: 'Due today', value: d.tasksDue || 0, icon: CheckSquare, from: '#60a5fa', to: '#2563eb', cap: 'follow-ups', go: '/employee/tasks' },
    { label: 'Won', value: d.won || 0, icon: Trophy, from: '#4ade80', to: '#0b8a4f', cap: 'closed by me', go: '/employee/performance' },
  ];
  const maxStage = Math.max(1, ...STAGES.map((s) => d.byStatus?.[s.key] || 0));

  return (
    <div className="space-y-5 max-w-[1180px] pt-1">
      <div>
        <h2 className="text-[20px] font-extrabold text-[var(--heading)] tracking-tight">
          {me.name ? `Hello, ${me.name.split(' ')[0]}` : 'Hello'}
        </h2>
        <p className="text-[13px] text-[var(--muted)]">Here's where your leads stand today.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <button key={k.label} onClick={() => nav(k.go)}
              className="rounded-3xl p-5 text-white card-lux text-left transition-transform hover:-translate-y-0.5"
              style={{ background: `linear-gradient(135deg, ${k.from}, ${k.to})` }}>
              <div className="flex items-center justify-center gap-2.5">
                <Icon size={22} className="opacity-95 shrink-0" />
                <div className="text-[19px] font-extrabold tracking-tight leading-tight truncate">{k.label}</div>
              </div>
              <div className="text-center mt-3">
                <div className="text-[32px] font-extrabold tracking-tight leading-none">{k.value}</div>
                <div className="text-[13px] font-bold mt-1.5" style={{ color: 'rgba(255,255,255,.88)' }}>{k.cap}</div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* my pipeline */}
        <div className="lg:col-span-2 bg-[var(--card)] rounded-3xl border border-[var(--border)] p-6 card-lux">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-extrabold text-[var(--text)] text-[17px] tracking-tight">My pipeline</h3>
            <span className="text-[12px] text-[var(--muted)]">{inr(d.pipelineValue)} open · {inr(d.wonValue)} won</span>
          </div>
          <div className="space-y-2">
            {STAGES.map((s) => {
              const n = d.byStatus?.[s.key] || 0;
              return (
                <button key={s.key} onClick={() => nav(`/employee/leads?view=list`)} className="w-full flex items-center gap-3 group">
                  <span className="w-[104px] text-left text-[12.5px] font-semibold text-[var(--muted)] shrink-0">{s.label}</span>
                  <span className="flex-1 h-7 rounded-lg overflow-hidden" style={{ background: 'var(--soft)' }}>
                    <span className="block h-full rounded-lg transition-all" style={{ width: `${Math.max(n ? 8 : 0, (n / maxStage) * 100)}%`, background: s.color }} />
                  </span>
                  <span className="w-7 text-right text-[13px] font-extrabold tabular-nums text-[var(--text)]">{n}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* due today */}
        <div className="bg-[var(--card)] rounded-3xl border border-[var(--border)] p-6 card-lux">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-extrabold text-[var(--text)] text-[17px] tracking-tight">Due today</h3>
            <button onClick={() => nav('/employee/tasks')} className="text-[12px] font-bold text-[var(--acc2)] hover:underline flex items-center gap-1">
              All <ArrowRight size={12} />
            </button>
          </div>
          {due.length === 0 ? (
            <div className="text-[13px] text-[var(--muted)] py-6 text-center">Nothing due today 🎉</div>
          ) : (
            <div className="space-y-2.5">
              {due.slice(0, 6).map((t) => {
                const TI = TICON[t.type] || CheckSquare;
                return (
                  <div key={t.id} className="flex items-start gap-2.5 text-[13px]">
                    <span className="w-6 h-6 rounded-lg shrink-0 flex items-center justify-center" style={{ background: 'var(--acc-soft)' }}>
                      <TI size={13} style={{ color: 'var(--acc2)' }} />
                    </span>
                    <div className="min-w-0">
                      <div className="font-semibold text-[var(--text)] truncate">{t.title}</div>
                      <div className="text-[11.5px] text-[var(--muted)] truncate">
                        {tLabel(t.type)}{t.Lead ? ` · ${t.Lead.name}` : ''}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
